import { create } from "zustand";
import type { ChatAttachment } from "../types";

/**
 * Chat store — composer UI state
 *
 * Mesaj geçmişinin gerçek kaynağı TanStack Query (backend) + streaming-store.
 * Bu store yalnızca chat input'unun UI state'ini yönetir:
 * yazılan mesaj, seçili ekler, focus isteği, input yüksekliği ve arama.
 */
export interface ChatStore {
  // ===== UI State =====
  isLoading: boolean;
  searchQuery: string;
  message: string;
  selectedAttachments: ChatAttachment[]; // Chat input'ta seçilen ekler
  // Input focus request token (increment to request focus without prop drilling)
  focusRequestToken: number;
  // ChatGPT-style: Input height for dynamic padding-bottom
  inputHeight: number;

  // ===== UI Actions =====
  setMessage: (message: string) => void;
  setIsLoading: (loading: boolean) => void;
  setSearchQuery: (query: string) => void;
  setSelectedAttachments: (attachments: ChatAttachment[]) => void;
  addSelectedAttachments: (attachments: ChatAttachment[]) => void;
  removeSelectedAttachment: (index: number) => void;
  clearSelectedAttachments: () => void;
  requestChatInputFocus: () => void;
  setInputHeight: (height: number) => void;

  // ===== Utils =====
  resetState: () => void;
}

export const useChatStore = create<ChatStore>()((set) => ({
  // ===== Initial State =====
  isLoading: false,
  searchQuery: "",
  message: "",
  selectedAttachments: [],
  focusRequestToken: 0,
  inputHeight: 0,

  // ===== UI Actions =====
  setMessage: (message: string) => set({ message }),
  setIsLoading: (loading: boolean) => set({ isLoading: loading }),
  setSearchQuery: (query: string) => set({ searchQuery: query }),
  setSelectedAttachments: (attachments: ChatAttachment[]) =>
    set({ selectedAttachments: attachments }),
  addSelectedAttachments: (attachments: ChatAttachment[]) =>
    set((state) => ({
      selectedAttachments: [...state.selectedAttachments, ...attachments],
    })),
  removeSelectedAttachment: (index: number) =>
    set((state) => ({
      selectedAttachments: state.selectedAttachments.filter(
        (_, i) => i !== index
      ),
    })),
  clearSelectedAttachments: () => set({ selectedAttachments: [] }),
  requestChatInputFocus: () =>
    set((state) => ({ focusRequestToken: state.focusRequestToken + 1 })),
  setInputHeight: (height: number) => set({ inputHeight: height }),

  // ===== Utils =====
  resetState: () => {
    set({
      isLoading: false,
      searchQuery: "",
      message: "",
      selectedAttachments: [],
    });
  },
}));

export default useChatStore;
